import { Agent } from "@/types/agent/agent";
import { PhoneCheckKind, PhoneCheckVerdict, RunType } from "@/types/agent/brand-integrity";
import { REQUEST_HUMAN_HANDOFF_TOOL_ID } from "@/utils/config/elevenlabs";

export const PHONE_TESTS_PATH = "/genie-hub/brand-integrity/phone-tests";

export const PHONE_CHECK_RUN_TYPES: Record<PhoneCheckKind, RunType> = {
  handoff: "handoff_check",
  transfer: "transfer_check",
};

export const PHONE_CHECK_LABELS: Record<PhoneCheckKind, string> = {
  handoff: "Handoff Check",
  transfer: "Transfer Check",
};

const PHONE_CHECK_KINDS = Object.keys(PHONE_CHECK_RUN_TYPES) as PhoneCheckKind[];

export const isPhoneCheckKind = (value: string | null | undefined): value is PhoneCheckKind =>
  PHONE_CHECK_KINDS.includes(value as PhoneCheckKind);

export const getPhoneCheckKind = (runType: string | null | undefined): PhoneCheckKind | null =>
  PHONE_CHECK_KINDS.find((kind) => PHONE_CHECK_RUN_TYPES[kind] === runType) ?? null;

export const getPhoneCheckPath = (agentId: string, kind: PhoneCheckKind) =>
  `${PHONE_TESTS_PATH}?agent=${encodeURIComponent(agentId)}&check=${kind}`;

/**
 * Why this Genie can't run the check yet, or null when it can. Mirrors the
 * setup the server requires (generate-phone-check), so the button is only
 * offered for a run that can start.
 */
export const getPhoneCheckBlocker = (kind: PhoneCheckKind, genie: Agent): string | null => {
  const promptConfig = genie.settings?.conversation_config?.agent?.prompt;
  const hasTransferNumber = (genie.transfer_phones?.length ?? 0) > 0;
  const hasConfiguredTransfers = (promptConfig?.built_in_tools?.transfer_to_number?.params?.transfers?.length ?? 0) > 0;

  if (kind === "transfer") {
    if (!hasTransferNumber) return "This Genie doesn't have a transfer number yet.";
    return hasConfiguredTransfers ? null : "This Genie's transfer numbers aren't configured yet. Open its Phone panel to fix that.";
  }

  // Live handoff calls the escalation numbers, or the transfer numbers when there are none.
  if ((genie.escalation_phones?.length ?? 0) === 0 && !hasTransferNumber) {
    return "This Genie doesn't have an escalation number yet.";
  }
  if (!genie.voice_bridge_enabled) return "Live Handoff from Browser is switched off for this Genie.";
  // A genie with transfer numbers starts the handoff with the phone transfer;
  // one without carries requestHumanHandoff (handle-agent's syncHumanHandoffTool).
  return hasConfiguredTransfers || promptConfig?.tool_ids?.includes(REQUEST_HUMAN_HANDOFF_TOOL_ID)
    ? null
    : "This Genie's live handoff isn't set up yet. Switch Live Handoff from Browser off and on again in its Phone panel.";
};

export interface PhoneCheckResult extends PhoneCheckVerdict {
  kind: PhoneCheckKind;
}

/** Reads the phone check verdict off a result's analysis_result, if it has one. */
export const getPhoneCheckResult = (analysis: Record<string, any> | null | undefined): PhoneCheckResult | null => {
  const kind = PHONE_CHECK_KINDS.find((candidate) => analysis?.[candidate]);
  return kind ? { kind, ...(analysis![kind] as PhoneCheckVerdict) } : null;
};

const formatDestination = (label?: string | null, phoneNumber?: string | null) =>
  label && phoneNumber && label !== phoneNumber ? `${label} (${phoneNumber})` : (label ?? phoneNumber ?? "");

export const describePhoneCheckExpectation = (result: PhoneCheckResult) => {
  if (result.kind === "handoff") return result.expected ? "Hand off" : "Not hand off";
  if (!result.expected) return "Not transfer";

  const destination = formatDestination(result.expected_label, result.expected_number);
  return destination ? `Transfer to ${destination}` : "Transfer";
};

export const describePhoneCheckOutcome = (result: PhoneCheckResult) => {
  if (!result.called) return result.kind === "handoff" ? "Did not hand off" : "Did not transfer";
  // In a website chat either person tool opens the Connect me prompt.
  if (result.kind === "handoff") return "Started a live handoff";
  if (result.tool_name !== "transfer_to_number") return "Handed off";

  return result.dialled_number ? `Transferred to ${result.dialled_number}` : "Transferred the call";
};
